/* RG Persistent Search
 * =====================
 * Custom element that turns server-rendered search scaffold into a
 * predictive-search experience.
 *
 * Contract with sections/rg-search-suggestions.liquid:
 *   Fetch:  /search/suggest?q=X&section_id=rg-search-suggestions
 *   Parse:  extract [data-rg-search-results] from response HTML
 *   Insert: replace contents of [data-rg-search-results-slot]
 *
 * State machine (data-state attribute):
 *   closed  - no focus, no typed query, panel hidden
 *   idle    - focused, empty query, shows recents + popular
 *   loading - fetching results
 *   results - showing fetched results (or zero-state)
 *
 * Mobile full-screen overlay is controlled by .is-open class, which
 * is toggled by [data-rg-search-trigger] buttons anywhere in the DOM.
 */

if (!customElements.get('rg-search')) {
  const FETCH_DEBOUNCE_MS = 200;
  const RECENTS_KEY = 'rg-search-recents';
  const RECENTS_MAX = 5;
  const BODY_OPEN_CLASS = 'rg-search-open';
  const REFERENCE_INDEX_SCHEMA_VERSION = 1;
  const SEARCH_INDEX_SCHEMA_VERSION = 1;

  class RGSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-rg-search-form]');
      this.input = this.querySelector('[data-rg-search-input]');
      this.clearBtn = this.querySelector('[data-rg-search-clear]');
      this.closeBtn = this.querySelector('[data-rg-search-close]');
      this.panel = this.querySelector('[data-rg-search-panel]');
      this.liveRegion = this.querySelector('[data-rg-search-live]');
      this.resultsSlot = this.querySelector('[data-rg-search-results-slot]');
      this.popularSlot = this.querySelector('[data-rg-search-popular-slot]');
      this.recentSection = this.querySelector('[data-rg-search-recent-section]');
      this.recentList = this.querySelector('[data-rg-search-recent-list]');
      this.recentClearBtn = this.querySelector('[data-rg-search-recent-clear]');

      if (!this.input || !this.panel || !this.resultsSlot) return;

      this.debounceId = -1;
      this.abortController = null;
      this.activeOptionIndex = -1;
      this.lastQuery = '';
      this.lastTrigger = null;
      this.popularCollectionHandle = (this.dataset.popularCollectionHandle || 'bestsellers').trim();
      this.popularLoaded = false;
      this.popularLoading = false;
      this.referenceIndexUrl = (this.dataset.referenceIndexUrl || '').trim();
      this.referenceIndexPromise = null;
      this.referenceIndexEntries = null;
      this.searchIndexUrl = (this.dataset.searchIndexUrl || '').trim();
      this.searchIndexPromise = null;
      this.searchIndexEntries = null;
      this.searchIndexSkuLookups = null;
      this.bypassSerialSubmit = false;
      this.fetchRequestId = 0;

      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      this.onOptionHover = this.onOptionHover.bind(this);
      this.onRecentClear = this.onRecentClear.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeyDown);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      if (this.clearBtn) this.clearBtn.addEventListener('click', this.onClear);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onClose);
      if (this.recentClearBtn) this.recentClearBtn.addEventListener('click', this.onRecentClear);

      this.panel.addEventListener('mousemove', this.onOptionHover);

      document.querySelectorAll('[data-rg-search-trigger]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.classList.contains('is-open')) {
            this.close();
            return;
          }
          this.lastTrigger = btn;
          this.closeMobileDrawerIfContaining(btn);
          this.open();
        });
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeyDown);

      this.renderRecents();
      this.updateClearVisibility();
      this.setState('closed');
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      document.body.classList.remove(BODY_OPEN_CLASS);
    }

    // ---------- State ----------

    setState(state) {
      this.setAttribute('data-state', state);
      const expanded = state !== 'closed';
      this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    setView(view) {
      this.setAttribute('data-view', view);
    }

    // ---------- Input handling ----------

    onInput() {
      const raw = this.input.value;
      const value = raw.trim();
      this.updateClearVisibility();

      if (value === this.lastQuery) return;
      this.lastQuery = value;

      clearTimeout(this.debounceId);

      if (!value) {
        this.abortPending();
        this.resultsSlot.innerHTML = '';
        this.setView('idle');
        this.setState('idle');
        this.liveRegion.textContent = '';
        this.ensurePopularLoaded();
        return;
      }

      this.setState('loading');
      this.debounceId = window.setTimeout(() => this.fetchResults(value), FETCH_DEBOUNCE_MS);
    }

    onFocus() {
      const value = this.input.value.trim();
      if (value) {
        this.setView('results');
        this.setState(this.resultsSlot.firstElementChild ? 'results' : 'idle');
      } else {
        this.setView('idle');
        this.setState('idle');
        this.ensurePopularLoaded();
      }
    }

    onSubmit(e) {
      const q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
        return;
      }
      if (this.bypassSerialSubmit) return;

      this.addToRecents(q);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'search',
          search_term: q,
        });
      }

      e.preventDefault();
      this.handleIdentifierSearchSubmit(q);
    }

    onClear() {
      this.input.value = '';
      this.lastQuery = '';
      this.updateClearVisibility();
      this.resultsSlot.innerHTML = '';
      this.liveRegion.textContent = '';
      this.setView('idle');
      this.setState('idle');
      this.ensurePopularLoaded();
      this.input.focus();
    }

    onClose() {
      this.close();
    }

    // ---------- Keyboard navigation ----------

    onKeyDown(e) {
      const options = this.getOptions();

      switch (e.key) {
        case 'ArrowDown':
          if (options.length === 0) return;
          e.preventDefault();
          this.setActiveOption(
            this.activeOptionIndex < options.length - 1 ? this.activeOptionIndex + 1 : 0
          );
          break;
        case 'ArrowUp':
          if (options.length === 0) return;
          e.preventDefault();
          this.setActiveOption(
            this.activeOptionIndex <= 0 ? options.length - 1 : this.activeOptionIndex - 1
          );
          break;
        case 'Home':
          if (options.length === 0) return;
          e.preventDefault();
          this.setActiveOption(0);
          break;
        case 'End':
          if (options.length === 0) return;
          e.preventDefault();
          this.setActiveOption(options.length - 1);
          break;
        case 'Enter': {
          const activeOpt = options[this.activeOptionIndex];
          if (activeOpt) {
            e.preventDefault();
            if (activeOpt.href) {
              window.location.href = activeOpt.href;
            } else {
              activeOpt.click();
            }
          }
          break;
        }
        case 'Escape':
          if (this.input.value) {
            e.preventDefault();
            this.onClear();
          } else {
            e.preventDefault();
            this.close();
          }
          break;
      }
    }

    onDocumentKeyDown(e) {
      if (e.key === 'Escape' && this.getAttribute('data-state') !== 'closed') {
        this.close();
      }
    }

    getOptions() {
      // offsetParent is null when an ancestor is display:none (our [data-view]
      // selectors hide popular/recents during results view and vice versa).
      return Array.from(this.querySelectorAll('[data-rg-search-option]')).filter(
        (el) => el.offsetParent !== null
      );
    }

    setActiveOption(index) {
      const options = this.getOptions();
      this.activeOptionIndex = index;

      options.forEach((opt, i) => {
        const active = i === index;
        opt.setAttribute('aria-selected', active ? 'true' : 'false');
        opt.classList.toggle('is-active', active);
      });

      const active = options[index];
      if (active) {
        if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onOptionHover(e) {
      const opt = e.target.closest('[data-rg-search-option]');
      if (!opt) return;
      const options = this.getOptions();
      const idx = options.indexOf(opt);
      if (idx !== -1 && idx !== this.activeOptionIndex) {
        this.setActiveOption(idx);
      }
    }

    // ---------- Fetching ----------

    abortPending() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    fetchResults(query) {
      this.abortPending();
      this.abortController = new AbortController();
      const controller = this.abortController;
      this.fetchRequestId += 1;
      const requestId = this.fetchRequestId;

      const route = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
      const url = `${route}?q=${encodeURIComponent(query)}&section_id=rg-search-suggestions&resources[type]=product,page&resources[limit]=6`;

      const predictiveResults = fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.querySelector('[data-rg-search-results]');
        })
        .catch((error) => {
          if (error.name === 'AbortError') throw error;
          // eslint-disable-next-line no-console
          console.warn('[rg-search]', error);
          return null;
        });

      const searchIndexResults = this.resolveSearchIndexEntries(query, 6).catch(() => []);
      const referenceResults = this.resolveReferenceSearchEntries(query).catch(() => []);
      const skuResults = this.resolveVariantSkuSearchEntries(query).catch(() => []);

      Promise.all([predictiveResults, searchIndexResults, referenceResults, skuResults])
        .then(([results, searchEntries, referenceEntries, skuEntries]) => {
          if (requestId !== this.fetchRequestId) return;

          this.resultsSlot.innerHTML = '';
          if (searchEntries.length) {
            this.renderSearchIndexResults(searchEntries, query, results);
          } else if (results) {
            this.resultsSlot.appendChild(results);
          }
          this.mergeReferenceResults(referenceEntries);
          this.mergeVariantSkuResults(skuEntries);
          this.rewriteIdentifierViewAllLink(query, Boolean(referenceEntries.length || skuEntries.length));

          this.activeOptionIndex = -1;
          this.input.removeAttribute('aria-activedescendant');
          this.setView('results');
          this.setState('results');

          const optionCount = this.resultsSlot.querySelectorAll('[data-rg-search-option]').length;
          if (optionCount === 0) {
            this.liveRegion.textContent = `No matches for ${query}`;
          } else {
            this.liveRegion.textContent = `${optionCount} result${optionCount === 1 ? '' : 's'} for ${query}`;
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.setState('idle');
          // eslint-disable-next-line no-console
          console.warn('[rg-search]', error);
        })
        .finally(() => {
          if (this.abortController === controller) {
            this.abortController = null;
          }
        });
    }

    isSerialLikeQuery(query) {
      const tokens = String(query || '').trim().split(/\s+/).filter(Boolean);
      if (tokens.length === 0) return false;
      if (!tokens.every((token) => /\d/.test(token))) return false;

      const normalized = tokens.join('').replace(/[^a-z0-9]/gi, '');
      return normalized.length >= 5;
    }

    isReferenceNumberLikeQuery(query) {
      const normalized = this.normalizeReferenceKey(query);

      for (let prefixLength = 1; prefixLength <= 3; prefixLength += 1) {
        const prefix = normalized.slice(0, prefixLength);
        const suffix = normalized.slice(prefixLength);
        if (
          prefix.length === prefixLength &&
          /[A-Z]/.test(prefix) &&
          /^[A-Z0-9]+$/.test(prefix) &&
          /^[0-9]{1,4}$/.test(suffix)
        ) {
          return true;
        }
      }

      return false;
    }

    isIdentifierLikeQuery(query) {
      return this.isSerialLikeQuery(query) || this.isReferenceNumberLikeQuery(query);
    }

    normalizeReferenceKey(value) {
      return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    handleIdentifierSearchSubmit(query) {
      return this.resolveIdentifierSearchProductLinks(query)
        .then((urls) => {
          if (urls.length === 1) {
            window.location.href = urls[0];
            return;
          }

          if (urls.length > 1) {
            this.open();
            this.fetchResults(query);
            return;
          }

          this.submitFormWithoutSerialIntercept();
        })
        .catch(() => {
          this.submitFormWithoutSerialIntercept();
        });
    }

    resolveIdentifierSearchProductLinks(query) {
      const lookups = [];

      lookups.push(this.resolveExactSearchIndexProductLinks(query).catch(() => []));

      if (this.isSerialLikeQuery(query)) {
        lookups.push(this.resolveSerialSearchProductLinks(query).catch(() => []));
      }

      if (this.isReferenceNumberLikeQuery(query)) {
        lookups.push(
          this.resolveReferenceSearchEntries(query)
            .then((entries) => entries.map((entry) => entry.url))
            .catch(() => [])
        );
      }

      lookups.push(
        this.resolveVariantSkuSearchEntries(query)
          .then((entries) => entries.map((entry) => entry.url))
          .catch(() => [])
      );

      if (lookups.length === 0) return Promise.resolve([]);

      return Promise.all(lookups).then((groups) => this.uniqueUrls(groups.flat()));
    }

    resolveExactSearchIndexProductLinks(query) {
      if (!window.RGSearchCore || !this.searchIndexUrl) return Promise.resolve([]);
      return this.loadSearchIndex().then((entries) =>
        window.RGSearchCore.findExactIdentifierMatches(entries, query)
          .map((entry) => entry.url)
          .filter(Boolean)
      );
    }

    resolveSerialSearchProductLinks(query) {
      const route = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
      const url = `${route}?q=${encodeURIComponent(query)}&section_id=rg-search-suggestions&resources[type]=product&resources[limit]=10`;

      return fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const productLinks = Array.from(doc.querySelectorAll('.rg-search__result-link'));
          return productLinks
            .map((link) => link.getAttribute('href') || '')
            .filter(Boolean);
        });
    }

    loadReferenceIndex() {
      if (!this.referenceIndexUrl) return Promise.resolve([]);
      if (this.referenceIndexEntries) return Promise.resolve(this.referenceIndexEntries);
      if (this.referenceIndexPromise) return this.referenceIndexPromise;

      this.referenceIndexPromise = fetch(this.referenceIndexUrl, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then((index) => {
          if (
            !index ||
            index.schema_version !== REFERENCE_INDEX_SCHEMA_VERSION ||
            !Array.isArray(index.entries)
          ) {
            return [];
          }

          this.referenceIndexEntries = index.entries.filter((entry) => this.isValidReferenceEntry(entry));
          return this.referenceIndexEntries;
        })
        .catch((error) => {
          this.referenceIndexEntries = [];
          // eslint-disable-next-line no-console
          console.warn('[rg-search reference index]', error);
          return [];
        });

      return this.referenceIndexPromise;
    }

    resolveReferenceSearchEntries(query) {
      if (!this.isReferenceNumberLikeQuery(query)) return Promise.resolve([]);
      const key = this.normalizeReferenceKey(query);

      return this.loadReferenceIndex().then((entries) =>
        entries
          .filter((entry) => entry.key === key)
          .sort((a, b) => this.compareReferenceEntries(a, b))
      );
    }

    loadSearchIndex() {
      if (!this.searchIndexUrl || !window.RGSearchCore) return Promise.resolve([]);
      if (this.searchIndexEntries) return Promise.resolve(this.searchIndexEntries);
      if (this.searchIndexPromise) return this.searchIndexPromise;

      this.searchIndexPromise = fetch(this.searchIndexUrl, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then((index) => {
          if (
            !index ||
            index.schema_version !== SEARCH_INDEX_SCHEMA_VERSION ||
            !Array.isArray(index.entries)
          ) {
            return [];
          }

          this.searchIndexEntries = index.entries.filter((entry) => this.isValidSearchIndexEntry(entry));
          this.searchIndexSkuLookups = Array.isArray(index.variant_sku_lookups)
            ? index.variant_sku_lookups.filter((entry) => this.isValidVariantSkuLookupEntry(entry))
            : [];
          return this.searchIndexEntries;
        })
        .catch((error) => {
          this.searchIndexEntries = [];
          this.searchIndexSkuLookups = [];
          // eslint-disable-next-line no-console
          console.warn('[rg-search search index]', error);
          return [];
        });

      return this.searchIndexPromise;
    }

    resolveSearchIndexEntries(query, limit) {
      if (!window.RGSearchCore) return Promise.resolve([]);
      return this.loadSearchIndex().then((entries) =>
        window.RGSearchCore.rankSearchEntries(entries, query, { limit })
      );
    }

    loadVariantSkuLookups() {
      return this.loadSearchIndex().then(() => this.searchIndexSkuLookups || []);
    }

    resolveVariantSkuSearchEntries(query) {
      if (!window.RGSearchCore) return Promise.resolve([]);
      return this.loadVariantSkuLookups().then((entries) =>
        window.RGSearchCore.findExactVariantSkuLookupMatches(entries, query)
      );
    }

    isValidSearchIndexEntry(entry) {
      return (
        entry &&
        typeof entry.entry_type === 'string' &&
        typeof entry.title === 'string' &&
        typeof entry.url === 'string' &&
        (entry.url.startsWith('/products/') || entry.url.startsWith('/variants/'))
      );
    }

    isValidReferenceEntry(entry) {
      return (
        entry &&
        entry.matched_identifier_type === 'reference_number' &&
        typeof entry.key === 'string' &&
        typeof entry.reference_number === 'string' &&
        typeof entry.product_title === 'string' &&
        typeof entry.url === 'string' &&
        entry.url.startsWith('/variants/')
      );
    }

    isValidVariantSkuLookupEntry(entry) {
      return (
        entry &&
        entry.matched_identifier_type === 'variant_sku' &&
        typeof entry.key === 'string' &&
        typeof entry.product_title === 'string' &&
        typeof entry.url === 'string' &&
        entry.url.startsWith('/variants/')
      );
    }

    compareReferenceEntries(a, b) {
      return (
        Number(Boolean(b.available)) - Number(Boolean(a.available)) ||
        a.product_title.localeCompare(b.product_title) ||
        String(a.variant_title || '').localeCompare(String(b.variant_title || '')) ||
        String(a.variant_id || '').localeCompare(String(b.variant_id || ''))
      );
    }

    mergeReferenceResults(referenceEntries) {
      if (!referenceEntries.length) return;

      const wrapper = this.ensureResultsWrapper();
      const zero = wrapper.querySelector('.rg-search__zero');
      if (zero) zero.remove();

      const list = this.ensureResultsList(wrapper);
      const existingKeys = new Set(
        Array.from(this.resultsSlot.querySelectorAll('.rg-search__result-link'))
          .map((link) => this.urlDedupeKey(link.getAttribute('href') || ''))
          .filter(Boolean)
      );
      const fragment = document.createDocumentFragment();
      let renderedCount = 0;

      referenceEntries.forEach((entry) => {
        const key = this.urlDedupeKey(entry.url);
        if (!key || existingKeys.has(key)) return;
        existingKeys.add(key);
        fragment.appendChild(this.createReferenceResultItem(entry, renderedCount));
        renderedCount += 1;
      });

      if (fragment.childNodes.length) {
        list.insertBefore(fragment, list.firstChild);
      }
    }

    mergeVariantSkuResults(skuEntries) {
      if (!skuEntries.length) return;

      const wrapper = this.ensureResultsWrapper();
      const zero = wrapper.querySelector('.rg-search__zero');
      if (zero) zero.remove();

      const list = this.ensureResultsList(wrapper);
      const existingKeys = new Set(
        Array.from(this.resultsSlot.querySelectorAll('.rg-search__result-link'))
          .map((link) => this.urlDedupeKey(link.getAttribute('href') || ''))
          .filter(Boolean)
      );
      const fragment = document.createDocumentFragment();
      let renderedCount = 0;

      skuEntries.forEach((entry) => {
        const key = this.urlDedupeKey(entry.url);
        if (!key || existingKeys.has(key)) return;
        existingKeys.add(key);
        fragment.appendChild(this.createVariantSkuResultItem(entry, renderedCount));
        renderedCount += 1;
      });

      if (fragment.childNodes.length) {
        list.insertBefore(fragment, list.firstChild);
      }
    }

    renderSearchIndexResults(entries, query, predictiveWrapper) {
      const wrapper = this.ensureResultsWrapper();
      const list = this.ensureResultsList(wrapper);
      const displaySpecs = this.specDisplayOptions(query);
      list.innerHTML = '';

      entries.forEach((entry, index) => {
        list.appendChild(this.createSearchIndexResultItem(entry, index, displaySpecs));
      });

      wrapper.appendChild(this.createViewAllLink(query));
      this.appendPredictivePagesLink(wrapper, predictiveWrapper);
    }

    ensureResultsWrapper() {
      let wrapper = this.resultsSlot.querySelector('[data-rg-search-results]');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'rg-search__results-wrapper';
        wrapper.setAttribute('data-rg-search-results', '');
        this.resultsSlot.appendChild(wrapper);
      }
      return wrapper;
    }

    ensureResultsList(wrapper) {
      let list = wrapper.querySelector('.rg-search__results');
      if (!list) {
        list = document.createElement('ul');
        list.className = 'rg-search__results';
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', 'Product suggestions');
        wrapper.insertBefore(list, wrapper.firstChild);
      }
      return list;
    }

    createReferenceResultItem(entry, index) {
      const item = document.createElement('li');
      item.className = 'rg-search__result';
      item.setAttribute('role', 'presentation');

      const link = document.createElement('a');
      link.className = 'rg-search__result-link';
      link.href = entry.url;
      link.setAttribute('role', 'option');
      link.id = `rg-search-reference-${index}`;
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('data-rg-search-option', '');
      link.setAttribute('data-rg-reference-result', '');

      const thumb = document.createElement('span');
      thumb.className = 'rg-search__result-thumb';
      thumb.setAttribute('aria-hidden', 'true');
      if (entry.image) {
        const img = document.createElement('img');
        img.src = entry.image;
        img.width = 60;
        img.height = 60;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      }

      const body = document.createElement('span');
      body.className = 'rg-search__result-body';
      const title = document.createElement('span');
      title.className = 'rg-search__result-title';
      title.textContent = entry.product_title;
      body.appendChild(title);
      body.appendChild(this.createReferenceSubtitle(`Reference Number ${entry.reference_number}`));

      const variantTitle = String(entry.variant_title || '').trim();
      if (variantTitle && variantTitle !== 'Default Title') {
        body.appendChild(this.createReferenceSubtitle(`Serial Number ${variantTitle}`));
      }

      link.appendChild(thumb);
      link.appendChild(body);
      item.appendChild(link);
      return item;
    }

    createSearchIndexResultItem(entry, index, displaySpecs = {}) {
      const item = document.createElement('li');
      item.className = 'rg-search__result';
      item.setAttribute('role', 'presentation');

      const link = document.createElement('a');
      link.className = 'rg-search__result-link';
      link.href = entry.url;
      link.setAttribute('role', 'option');
      link.id = `rg-search-index-${index}`;
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('data-rg-search-option', '');
      link.setAttribute('data-rg-search-index-result', '');

      const thumb = document.createElement('span');
      thumb.className = 'rg-search__result-thumb';
      thumb.setAttribute('aria-hidden', 'true');
      if (entry.image) {
        const img = document.createElement('img');
        img.src = entry.image;
        img.width = 60;
        img.height = 60;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      }

      const body = document.createElement('span');
      body.className = 'rg-search__result-body';
      const title = document.createElement('span');
      title.className = 'rg-search__result-title';
      title.textContent = entry.title;
      body.appendChild(title);

      const subtitleParts = [entry.vendor, entry.product_type].filter(Boolean);
      if (subtitleParts.length) {
        body.appendChild(this.createReferenceSubtitle(subtitleParts.join(' · ')));
      }
      if (entry.reference_number) {
        body.appendChild(this.createReferenceSubtitle(`Reference Number ${entry.reference_number}`));
      }
      if (entry.serial_number) {
        body.appendChild(this.createReferenceSubtitle(`Serial Number ${entry.serial_number}`));
      }
      if (displaySpecs.weight && entry.entry_type === 'guitar_listing' && entry.weight_display) {
        body.appendChild(this.createReferenceSubtitle(`Weight ${entry.weight_display}`));
      }
      if (displaySpecs.nutWidth && entry.entry_type === 'guitar_listing' && entry.nut_width_display) {
        body.appendChild(this.createReferenceSubtitle(`Nut Width ${entry.nut_width_display}`));
      }

      link.appendChild(thumb);
      link.appendChild(body);

      if (entry.price) {
        const price = document.createElement('span');
        price.className = 'rg-search__result-price';
        price.textContent = this.formatPrice(entry.price);
        link.appendChild(price);
      }

      item.appendChild(link);
      return item;
    }

    createVariantSkuResultItem(entry, index) {
      const item = document.createElement('li');
      item.className = 'rg-search__result';
      item.setAttribute('role', 'presentation');

      const link = document.createElement('a');
      link.className = 'rg-search__result-link';
      link.href = entry.url;
      link.setAttribute('role', 'option');
      link.id = `rg-search-sku-${index}`;
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('data-rg-search-option', '');
      link.setAttribute('data-rg-sku-result', '');

      const thumb = document.createElement('span');
      thumb.className = 'rg-search__result-thumb';
      thumb.setAttribute('aria-hidden', 'true');
      if (entry.image) {
        const img = document.createElement('img');
        img.src = entry.image;
        img.width = 60;
        img.height = 60;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      }

      const body = document.createElement('span');
      body.className = 'rg-search__result-body';
      const title = document.createElement('span');
      title.className = 'rg-search__result-title';
      title.textContent = entry.product_title || entry.title || '';
      body.appendChild(title);

      const variantTitle = String(entry.variant_title || '').trim();
      if (variantTitle && variantTitle !== 'Default Title') {
        body.appendChild(this.createReferenceSubtitle(variantTitle));
      }

      link.appendChild(thumb);
      link.appendChild(body);

      if (entry.price) {
        const price = document.createElement('span');
        price.className = 'rg-search__result-price';
        price.textContent = this.formatPrice(entry.price);
        link.appendChild(price);
      }

      item.appendChild(link);
      return item;
    }

    createViewAllLink(query) {
      const link = document.createElement('a');
      link.className = 'rg-search__view-all';
      link.href = `/search?q=${encodeURIComponent(query)}&type=product`;
      link.setAttribute('role', 'option');
      link.id = 'rg-search-option-view-all';
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('data-rg-search-option', '');
      link.innerHTML = `<span>See all results for <strong>${this.escapeHtml(query)}</strong></span><svg viewBox="0 0 24 24" aria-hidden="true" width="14" height="14"><path d="M5 12h14M12 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      return link;
    }

    appendPredictivePagesLink(wrapper, predictiveWrapper) {
      if (!predictiveWrapper) return;
      const pagesLink = predictiveWrapper.querySelector('.rg-search__pages-link');
      if (!pagesLink) return;
      const clone = pagesLink.cloneNode(true);
      clone.id = 'rg-search-option-pages';
      wrapper.appendChild(clone);
    }

    createReferenceSubtitle(text) {
      const subtitle = document.createElement('span');
      subtitle.className = 'rg-search__result-subtitle';
      subtitle.textContent = text;
      return subtitle;
    }

    specDisplayOptions(query) {
      if (!window.RGSearchCore || typeof window.RGSearchCore.parseSpecFilteredQuery !== 'function') {
        return { weight: false, nutWidth: false };
      }

      const parsed = window.RGSearchCore.parseSpecFilteredQuery(query);
      if (!parsed || !parsed.displaySpecs) return { weight: false, nutWidth: false };
      return parsed.displaySpecs;
    }

    formatPrice(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: number % 1 === 0 ? 0 : 2,
      }).format(number);
    }

    escapeHtml(value) {
      const span = document.createElement('span');
      span.textContent = value;
      return span.innerHTML;
    }

    rewriteIdentifierViewAllLink(query, forceIdentifierViewAll) {
      if (!forceIdentifierViewAll && !this.isIdentifierLikeQuery(query)) return;

      const productLinks = Array.from(this.resultsSlot.querySelectorAll('.rg-search__result-link'));
      const viewAllLink = this.resultsSlot.querySelector('.rg-search__view-all');
      if (!viewAllLink) return;

      if (productLinks.length > 1) {
        this.disableSerialViewAllLink(viewAllLink);
        return;
      }

      if (productLinks.length !== 1) return;

      const productHref = productLinks[0].getAttribute('href');
      if (productHref) viewAllLink.setAttribute('href', productHref);
    }

    uniqueUrls(urls) {
      const seen = new Set();
      const unique = [];

      urls.forEach((url) => {
        const key = this.urlDedupeKey(url);
        if (!key || seen.has(key)) return;
        seen.add(key);
        unique.push(url);
      });

      return unique;
    }

    urlDedupeKey(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        const variantFromPath = parsed.pathname.match(/^\/variants\/([^/]+)$/);
        if (variantFromPath) return `variant:${variantFromPath[1]}`;
        const variantFromQuery = parsed.searchParams.get('variant');
        if (variantFromQuery) return `variant:${variantFromQuery}`;
        return `${parsed.pathname}${parsed.search}`;
      } catch (_) {
        return '';
      }
    }

    disableSerialViewAllLink(link) {
      if (typeof link.remove === 'function') {
        link.remove();
      } else {
        link.setAttribute('hidden', '');
        link.setAttribute('aria-hidden', 'true');
      }
    }

    submitFormWithoutSerialIntercept() {
      if (!this.form) return;
      this.bypassSerialSubmit = true;

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }

    fetchPopularSection(handle) {
      const safeHandle = String(handle || 'all').toLowerCase().replace(/[^a-z0-9-]/g, '') || 'all';
      const url = `/collections/${encodeURIComponent(safeHandle)}?section_id=rg-search-popular`;

      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) return null;
          return response.text();
        })
        .then((html) => {
          if (!html) return null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.querySelector('[data-rg-search-popular-section]');
        });
    }

    ensurePopularLoaded() {
      if (!this.popularSlot || this.popularLoaded || this.popularLoading) return;

      const primaryHandle = this.popularCollectionHandle || 'bestsellers';
      this.popularLoading = true;

      this.fetchPopularSection(primaryHandle)
        .then((section) => {
          if (section || primaryHandle === 'all') return section;
          return this.fetchPopularSection('all');
        })
        .then((section) => {
          if (section) this.popularSlot.replaceChildren(section);
          this.popularLoaded = true;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.warn('[rg-search popular]', error);
        })
        .finally(() => {
          this.popularLoading = false;
        });
    }

    // ---------- Open / close ----------

    open() {
      this.classList.add('is-open');
      document.body.classList.add(BODY_OPEN_CLASS);
      this.syncTriggers(true);
      this.setView(this.input.value.trim() ? 'results' : 'idle');
      this.setState(this.input.value.trim() ? 'results' : 'idle');
      if (!this.input.value.trim()) this.ensurePopularLoaded();
      // Defer focus so iOS Safari actually opens the keyboard.
      window.requestAnimationFrame(() => this.input.focus());
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove(BODY_OPEN_CLASS);
      this.syncTriggers(false);
      this.setState('closed');
      this.input.removeAttribute('aria-activedescendant');
      this.activeOptionIndex = -1;
      if (this.lastTrigger) {
        this.lastTrigger.focus();
        this.lastTrigger = null;
      }
    }

    syncTriggers(isOpen) {
      document.querySelectorAll('[data-rg-search-trigger]').forEach((btn) => {
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    onDocumentClick(e) {
      if (this.getAttribute('data-state') === 'closed') return;
      if (this.contains(e.target)) return;
      if (e.target.closest('[data-rg-search-trigger]')) return;
      this.close();
    }

    // When the user taps a trigger inside the mobile drawer, dismiss the
    // drawer before opening search so only one overlay is active.
    closeMobileDrawerIfContaining(btn) {
      const drawer = document.querySelector('[data-mobile-drawer]');
      if (!drawer || !drawer.contains(btn)) return;
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      const toggle = document.querySelector('[data-mobile-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
      }
      document.body.style.overflow = '';
    }

    updateClearVisibility() {
      if (!this.clearBtn) return;
      if (this.input.value.length > 0) {
        this.clearBtn.removeAttribute('hidden');
      } else {
        this.clearBtn.setAttribute('hidden', '');
      }
    }

    // ---------- Recent searches ----------

    getRecents() {
      try {
        const raw = window.localStorage.getItem(RECENTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
      } catch (_) {
        return [];
      }
    }

    setRecents(list) {
      try {
        window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
      } catch (_) {
        // storage disabled/full — non-fatal
      }
    }

    addToRecents(term) {
      const needle = term.toLowerCase();
      const filtered = this.getRecents().filter((t) => t.toLowerCase() !== needle);
      this.setRecents([term, ...filtered].slice(0, RECENTS_MAX));
    }

    onRecentClear(e) {
      e.preventDefault();
      this.setRecents([]);
      this.renderRecents();
      this.input.focus();
    }

    renderRecents() {
      if (!this.recentSection || !this.recentList) return;

      const recents = this.getRecents();
      if (recents.length === 0) {
        this.recentSection.setAttribute('hidden', '');
        this.recentList.innerHTML = '';
        return;
      }

      this.recentSection.removeAttribute('hidden');
      this.recentList.innerHTML = recents
        .map(
          (term, i) => `
          <li role="presentation">
            <a
              href="/search?q=${encodeURIComponent(term)}"
              class="rg-search__recent-link"
              data-rg-search-option
              role="option"
              id="rg-search-recent-${i}"
              aria-selected="false"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M12 7.5v4.5l3 2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              <span>${this.escapeHtml(term)}</span>
            </a>
          </li>`
        )
        .join('');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  window.customElements.define('rg-search', RGSearch);
}
